/**
 * AssignKPIModal — Create/assign a KPI from template or custom.
 *
 * Step 1 "Define": core fields (name, description, targets, measurement settings,
 *   formula, verifier, financial impact).
 * Step 2 "Assign": pick assignees from org users.
 *
 * When loaded from a template, measurement fields are pre-filled but editable.
 * When custom, they default to sensible values and the user sets them.
 */
import React, { useState, useEffect, useMemo } from 'react';
import Target from 'lucide-react/dist/esm/icons/target';
import DollarSign from 'lucide-react/dist/esm/icons/dollar-sign';
import TrendingUp from 'lucide-react/dist/esm/icons/trending-up';
import ShieldCheck from 'lucide-react/dist/esm/icons/shield-check';
import UserCheck from 'lucide-react/dist/esm/icons/user-check';
import UserMinus from 'lucide-react/dist/esm/icons/user-minus';
import X from 'lucide-react/dist/esm/icons/x';
import { Modal, Button, TextField, TextArea, SearchInput, Select, Alert, Switch } from '../../../components/ui';
import type { KPIDefinition } from '../../../services/api/kpi';
import { useKPIStore } from '../../../stores/kpiStore';
import { useOrgStore } from '../../../stores/orgStore';

const UNIT_OPTIONS = [
  { value: 'currency', label: 'Currency ($)' },
  { value: 'percentage', label: 'Percentage (%)' },
  { value: 'count', label: 'Count' },
  { value: 'score', label: 'Score' },
  { value: 'hours', label: 'Hours' },
  { value: 'ratio', label: 'Ratio' },
  { value: 'custom', label: 'Custom' },
];

const CATEGORY_OPTIONS = [
  { value: 'financial', label: 'Financial' },
  { value: 'sales', label: 'Sales' },
  { value: 'marketing', label: 'Marketing' },
  { value: 'operations', label: 'Operations' },
  { value: 'product', label: 'Product' },
  { value: 'hr', label: 'HR & People' },
  { value: 'customer_success', label: 'Customer Success' },
  { value: 'custom', label: 'Custom' },
];

const CADENCE_OPTIONS = [
  { value: 'weekly', label: 'Weekly' },
  { value: 'biweekly', label: 'Bi-weekly' },
  { value: 'monthly', label: 'Monthly' },
  { value: 'quarterly', label: 'Quarterly' },
];

const DIRECTION_OPTIONS = [
  { value: 'higher_better', label: 'Higher is better' },
  { value: 'lower_better', label: 'Lower is better' },
  { value: 'target_exact', label: 'Hit exact target' },
];

const UNIT_LABELS: Record<string, string> = {
  currency: '$', percentage: '%', count: '', score: '/10',
  hours: 'hrs', ratio: 'x', custom: '',
};

interface AssignKPIModalProps {
  isOpen: boolean;
  onClose: () => void;
  template: KPIDefinition | null;
}

const AssignKPIModal: React.FC<AssignKPIModalProps> = ({ isOpen, onClose, template }) => {
  const { createDefinition, createAssignment, activeCycleId, fetchMyDashboard, fetchTeamDashboard } = useKPIStore();
  const { users, fetchUsers } = useOrgStore();

  // Core fields
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [targetValue, setTargetValue] = useState('');
  const [baselineValue, setBaselineValue] = useState('');
  const [weight, setWeight] = useState('100');

  // Measurement settings
  const [unit, setUnit] = useState('count');
  const [unitLabel, setUnitLabel] = useState('');
  const [category, setCategory] = useState('custom');
  const [cadence, setCadence] = useState('monthly');
  const [directionality, setDirectionality] = useState('higher_better');
  const [formulaDescription, setFormulaDescription] = useState('');

  // Verifier
  const [verifierId, setVerifierId] = useState('');
  const [verifierSearch, setVerifierSearch] = useState('');
  const [showVerifierPicker, setShowVerifierPicker] = useState(false);

  // Financial impact
  const [financialImpactTarget, setFinancialImpactTarget] = useState('');
  const [showFinancialInput, setShowFinancialInput] = useState(false);

  // Assign step
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set());
  const [searchUsers, setSearchUsers] = useState('');

  // UI
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [step, setStep] = useState<'define' | 'assign'>('define');

  const hasFinancialContext = !!(template?.financial_impact_type || template?.financial_impact_description);

  useEffect(() => {
    if (isOpen) {
      fetchUsers();
      if (template) {
        setName(template.name);
        setDescription(template.description || '');
        setTargetValue(template.default_target?.toString() || '');
        setBaselineValue(template.default_baseline?.toString() || '');
        setUnit(template.unit || 'count');
        setUnitLabel(template.unit_label || UNIT_LABELS[template.unit || 'count'] || '');
        setCategory(template.category || 'custom');
        setCadence(template.cadence || 'monthly');
        setDirectionality(template.directionality || 'higher_better');
        setFormulaDescription(template.formula_description || '');
      } else {
        setName('');
        setDescription('');
        setTargetValue('');
        setBaselineValue('');
        setUnit('count');
        setUnitLabel('');
        setCategory('custom');
        setCadence('monthly');
        setDirectionality('higher_better');
        setFormulaDescription('');
      }
      setWeight('100');
      setVerifierId('');
      setVerifierSearch('');
      setShowVerifierPicker(false);
      setFinancialImpactTarget('');
      setShowFinancialInput(hasFinancialContext);
      setSelectedUserIds(new Set());
      setSearchUsers('');
      setError(null);
      setSaving(false);
      setStep('define');
    }
  }, [isOpen, template]); // eslint-disable-line react-hooks/exhaustive-deps

  // Keep unit label in sync when unit changes (unless user has customized it)
  const handleUnitChange = (val: string) => {
    setUnit(val);
    setUnitLabel(UNIT_LABELS[val] || '');
  };

  const filteredUsers = useMemo(() => {
    const q = searchUsers.toLowerCase();
    const active = users.filter(u => u.status !== 'deactivated');
    if (!q) return active;
    return active.filter(u =>
      (u.display_name || '').toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    );
  }, [users, searchUsers]);

  const verifierCandidates = useMemo(() => {
    const q = verifierSearch.toLowerCase();
    const active = users.filter(u => u.status !== 'deactivated');
    if (!q) return active.slice(0, 8);
    return active.filter(u =>
      (u.display_name || '').toLowerCase().includes(q) ||
      u.email.toLowerCase().includes(q)
    ).slice(0, 8);
  }, [users, verifierSearch]);

  const selectedVerifier = useMemo(
    () => users.find(u => u.id === verifierId),
    [users, verifierId],
  );

  const toggleUser = (userId: string) => {
    setSelectedUserIds(prev => {
      const next = new Set(prev);
      if (next.has(userId)) next.delete(userId);
      else next.add(userId);
      return next;
    });
  };

  const handleAssign = async () => {
    if (!name.trim() || !activeCycleId || selectedUserIds.size === 0) return;
    setSaving(true);
    setError(null);

    try {
      const defn = await createDefinition({
        name: name.trim(),
        description: description.trim() || undefined,
        template_id: template?.id,
        default_target: targetValue ? parseFloat(targetValue) : undefined,
        default_baseline: baselineValue ? parseFloat(baselineValue) : undefined,
        category,
        kpi_type: template?.kpi_type || 'quantitative',
        unit,
        unit_label: unitLabel || undefined,
        directionality,
        cadence,
        thresholds: template?.thresholds || undefined,
        formula_description: formulaDescription.trim() || undefined,
        business_question: template?.business_question || undefined,
        financial_impact_type: template?.financial_impact_type || undefined,
        financial_impact_description: template?.financial_impact_description || undefined,
      });

      const fiTarget = financialImpactTarget ? parseFloat(financialImpactTarget) : undefined;
      const userIds = Array.from(selectedUserIds);
      for (let i = 0; i < userIds.length; i++) {
        await createAssignment({
          definition_id: defn.id,
          cycle_id: activeCycleId,
          assignee_id: userIds[i],
          target_value: targetValue ? parseFloat(targetValue) : undefined,
          baseline_value: baselineValue ? parseFloat(baselineValue) : undefined,
          weight: parseInt(weight) || 100,
          financial_impact_target: fiTarget,
          verifier_id: verifierId || undefined,
        });
      }

      await Promise.all([fetchMyDashboard(), fetchTeamDashboard()]);
      onClose();
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Failed to assign KPI');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <Modal.Header
        title={template ? `Assign: ${template.name}` : 'Create & Assign KPI'}
        icon={<Target size={16} className="text-status-success" />}
        onClose={onClose}
      />

      {/* Steps indicator */}
      <div className="flex items-center gap-2 px-6 pb-3 pt-1">
        <button
          onClick={() => setStep('define')}
          className={`text-xs font-medium px-2 py-1 rounded cursor-pointer border-none outline-none ${step === 'define' ? 'text-primary' : 'text-content-muted'}`}
        >
          1. Define
        </button>
        <span className="text-content-faint">→</span>
        <button
          onClick={() => step !== 'define' && setStep('assign')}
          className={`text-xs font-medium px-2 py-1 rounded cursor-pointer border-none outline-none ${step === 'assign' ? 'text-primary' : 'text-content-muted'}`}
        >
          2. Assign
        </button>
      </div>

      <Modal.Body>
        {step === 'define' && (
          <div className="space-y-4">
            {/* Name + Description */}
            <TextField
              label="KPI Name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g., Revenue Growth Rate, Net Value Creation..."
              autoFocus
            />
            <TextArea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="What does this KPI measure and why does it matter?"
              resize="none"
            />

            {/* Measurement Settings */}
            <div className="space-y-3 p-4 rounded-xl border border-subtle bg-surface-primary">
              <p className="text-[10px] text-content-muted uppercase tracking-wider font-semibold">Measurement</p>
              <div className="grid grid-cols-3 gap-3">
                <Select
                  value={category}
                  onChange={setCategory}
                  options={CATEGORY_OPTIONS}
                />
                <Select
                  value={unit}
                  onChange={handleUnitChange}
                  options={UNIT_OPTIONS}
                />
                <Select
                  value={directionality}
                  onChange={setDirectionality}
                  options={DIRECTION_OPTIONS}
                />
              </div>
              <div className="grid grid-cols-3 gap-3">
                <TextField
                  label="Target"
                  type="number"
                  step="any"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  placeholder={template?.default_target?.toString() || '—'}
                />
                <TextField
                  label="Baseline"
                  type="number"
                  step="any"
                  value={baselineValue}
                  onChange={(e) => setBaselineValue(e.target.value)}
                  placeholder={template?.default_baseline?.toString() || '0'}
                />
                <TextField
                  label="Weight"
                  type="number"
                  min={1}
                  max={100}
                  value={weight}
                  onChange={(e) => setWeight(e.target.value)}
                />
              </div>
              {unit === 'custom' && (
                <TextField
                  label="Unit Label"
                  value={unitLabel}
                  onChange={(e) => setUnitLabel(e.target.value)}
                  placeholder="e.g., deliverables, departments, milestones..."
                />
              )}
            </div>

            {/* Check-in Cadence */}
            <div className="p-4 rounded-xl border border-subtle bg-surface-primary space-y-2">
              <p className="text-[10px] text-content-muted uppercase tracking-wider font-semibold">Check-in Frequency</p>
              <Select
                value={cadence}
                onChange={setCadence}
                options={CADENCE_OPTIONS}
              />
              <p className="text-[11px] text-content-muted">
                How often the assignee is expected to report progress. This is not the cycle — the cycle defines the overall period (e.g. Q1 2026), while cadence sets how many check-ins happen within it.
              </p>
            </div>

            {/* Formula Description */}
            <TextArea
              label="Formula / Calculation Method"
              value={formulaDescription}
              onChange={(e) => setFormulaDescription(e.target.value)}
              rows={2}
              placeholder="How is this KPI calculated? What's included/excluded?"
              resize="none"
            />

            {/* Verifier */}
            <div className="p-4 rounded-xl border border-subtle bg-surface-primary space-y-2">
              <div className="flex items-center gap-2">
                <ShieldCheck size={14} className={verifierId ? 'text-blue-400' : 'text-content-muted'} />
                <p className="text-[10px] text-content-muted uppercase tracking-wider font-semibold flex-1">
                  Check-in Verifier
                </p>
                <span className="text-[11px] text-content-muted">Optional</span>
              </div>
              {selectedVerifier ? (
                <div className="flex items-center gap-2 px-3 py-2 rounded-lg bg-blue-400/[0.06] border border-blue-400/15">
                  <div className="w-6 h-6 rounded-full bg-blue-400/20 flex items-center justify-center text-[10px] font-bold text-blue-400 shrink-0">
                    {(selectedVerifier.display_name || selectedVerifier.email)[0]?.toUpperCase()}
                  </div>
                  <div className="flex-1 min-w-0">
                    <span className="text-xs font-medium text-content">{selectedVerifier.display_name || selectedVerifier.email}</span>
                    {selectedVerifier.title && <span className="text-[11px] text-content-muted ml-1.5">{selectedVerifier.title}</span>}
                  </div>
                  <button
                    type="button"
                    onClick={() => { setVerifierId(''); setShowVerifierPicker(false); }}
                    className="text-content-muted hover:text-content-tertiary cursor-pointer border-none outline-none p-0.5"
                  >
                    <X size={12} />
                  </button>
                </div>
              ) : (
                <>
                  {!showVerifierPicker ? (
                    <button
                      type="button"
                      onClick={() => setShowVerifierPicker(true)}
                      className="w-full px-3 py-2 rounded-lg border border-dashed border-subtle text-xs text-content-muted hover:text-content-tertiary hover:border-default transition-colors cursor-pointer outline-none text-left"
                    >
                      Choose who validates check-ins (e.g. Finance, a manager)...
                    </button>
                  ) : (
                    <div className="space-y-2">
                      <SearchInput
                        value={verifierSearch}
                        onChange={setVerifierSearch}
                        onClear={() => setVerifierSearch('')}
                        placeholder="Search verifier..."
                      />
                      <div className="space-y-0.5 max-h-[160px] overflow-y-auto">
                        {verifierCandidates.map(u => (
                          <button
                            key={u.id}
                            type="button"
                            onClick={() => { setVerifierId(u.id); setShowVerifierPicker(false); setVerifierSearch(''); }}
                            className="w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-left hover:bg-surface-secondary transition-colors cursor-pointer border-none outline-none"
                          >
                            <div className="w-6 h-6 rounded-full bg-surface-secondary flex items-center justify-center text-[10px] font-bold text-content-muted shrink-0">
                              {(u.display_name || u.email)[0]?.toUpperCase()}
                            </div>
                            <div className="flex-1 min-w-0">
                              <span className="text-xs text-content truncate block">{u.display_name || u.email.split('@')[0]}</span>
                              <span className="text-[11px] text-content-muted truncate block">{u.title || u.email}</span>
                            </div>
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {/* Financial Impact */}
            <div className={`rounded-xl border transition-colors ${showFinancialInput ? 'border-emerald-400/20 bg-emerald-400/[0.04]' : 'border-subtle bg-surface-primary'}`}>
              <button
                type="button"
                onClick={() => {
                  const next = !showFinancialInput;
                  setShowFinancialInput(next);
                  if (!next) setFinancialImpactTarget('');
                }}
                className="w-full flex items-center gap-3 px-4 py-3 text-left cursor-pointer border-none outline-none rounded-xl"
              >
                <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 transition-colors ${showFinancialInput ? 'bg-emerald-400/15' : 'bg-surface-secondary'}`}>
                  <DollarSign size={15} className={showFinancialInput ? 'text-emerald-400' : 'text-content-muted'} />
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`text-sm font-medium ${showFinancialInput ? 'text-emerald-400' : 'text-content-secondary'}`}>
                    Track Financial Impact
                  </p>
                  <p className="text-[11px] text-content-muted mt-0.5">
                    {showFinancialInput ? 'Set the expected monetary value this KPI will generate or save' : 'Optional — link this KPI to a revenue or savings target'}
                  </p>
                </div>
                <span onClick={(e) => e.stopPropagation()}>
                  <Switch
                    checked={showFinancialInput}
                    onChange={() => {
                      const next = !showFinancialInput;
                      setShowFinancialInput(next);
                      if (!next) setFinancialImpactTarget('');
                    }}
                    size="md"
                  />
                </span>
              </button>
              {showFinancialInput && (
                <div className="px-4 pb-4 space-y-3 border-t border-emerald-400/10">
                  <div className="pt-3">
                    <TextField
                      label="Expected Financial Impact ($)"
                      type="number"
                      step="any"
                      value={financialImpactTarget}
                      onChange={(e) => setFinancialImpactTarget(e.target.value)}
                      placeholder="e.g., 50 000"
                    />
                  </div>
                  {template?.financial_impact_description ? (
                    <div className="flex items-start gap-2 px-3 py-2.5 rounded-lg bg-emerald-400/[0.06] border border-emerald-400/10">
                      <TrendingUp size={13} className="text-emerald-400 mt-0.5 shrink-0" />
                      <p className="text-xs text-content-secondary leading-relaxed">{template.financial_impact_description}</p>
                    </div>
                  ) : (
                    <p className="text-[11px] text-content-muted">
                      This value will track against actual impact reported in each check-in, giving you a running ROI view.
                    </p>
                  )}
                </div>
              )}
            </div>

            {template && (
              <div className="p-3 rounded-xl bg-brand/[0.04] border border-brand/10 text-xs text-content-muted">
                From template: <span className="text-brand/60">{template.name}</span>
                {template.financial_impact_type && (
                  <span className="text-emerald-400/70 ml-1">
                    · {template.financial_impact_type.replace('_', ' ')}
                  </span>
                )}
              </div>
            )}
          </div>
        )}

        {step === 'assign' && (
          <div className="space-y-3">
            <SearchInput
              value={searchUsers}
              onChange={setSearchUsers}
              onClear={() => setSearchUsers('')}
              placeholder="Search team members..."
            />
            <div className="flex items-center justify-between text-[11px] text-content-tertiary">
              <span>{selectedUserIds.size} selected</span>
              <span>{filteredUsers.length} available</span>
            </div>
            <div className="space-y-1 max-h-[300px] overflow-y-auto pr-1 -mr-1">
              {filteredUsers.map(user => {
                const isSelected = selectedUserIds.has(user.id);
                return (
                  <button
                    key={user.id}
                    type="button"
                    onClick={() => toggleUser(user.id)}
                    className={`w-full flex items-center gap-3 p-2.5 rounded-xl text-left transition-all cursor-pointer border outline-none
                      ${isSelected
                        ? 'bg-brand/10 border-brand/20 hover:bg-brand/15'
                        : 'bg-transparent border-transparent hover:bg-surface-primary hover:border-subtle'
                      }
                    `}
                  >
                    <div className={`w-7 h-7 rounded-full flex items-center justify-center text-[10px] font-bold shrink-0 transition-colors
                      ${isSelected ? 'bg-brand/30 text-primary' : 'bg-surface-secondary text-content-muted'}
                    `}>
                      {(user.display_name || user.email)[0]?.toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs font-medium text-content truncate">{user.display_name || user.email.split('@')[0]}</div>
                      <div className="text-[11px] text-content-muted truncate">{user.title || user.email}</div>
                    </div>
                    {isSelected ? <UserCheck size={14} className="text-primary shrink-0" /> : <UserMinus size={14} className="text-content-faint shrink-0" />}
                  </button>
                );
              })}
            </div>
          </div>
        )}
      </Modal.Body>

      <Modal.Footer className="justify-between">
        {error ? <Alert variant="error" className="flex-1 mr-3">{error}</Alert> : <div />}
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="lg" onClick={onClose}>Cancel</Button>
          {step === 'define' ? (
            <Button
              variant="outline"
              size="lg"
              onClick={() => setStep('assign')}
              disabled={!name.trim()}
            >
              Next: Pick People
            </Button>
          ) : (
            <Button
              variant="success"
              size="lg"
              onClick={handleAssign}
              loading={saving}
              disabled={selectedUserIds.size === 0 || !activeCycleId}
            >
              Assign KPI to {selectedUserIds.size} {selectedUserIds.size === 1 ? 'person' : 'people'}
            </Button>
          )}
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default React.memo(AssignKPIModal);
